/**
 * Asset abstraction. The renderer depends only on this interface, never on
 * concrete art — so procedurally-generated placeholders (below) can be swapped
 * for a licensed hand-painted texture atlas with zero renderer changes.
 *
 * For Sprint 1 tiles are flat-shaded iso diamonds with a top + two side faces to
 * fake elevation. `TileFaces` returns those three colours per terrain type.
 */
import { TerrainType, DepositType } from '@settlers/shared';

export interface TileFaces {
  /** Top diamond face. */
  readonly top: number;
  /** Left vertical face (for elevated tiles). */
  readonly left: number;
  /** Right vertical face (for elevated tiles). */
  readonly right: number;
}

export interface AssetProvider {
  readonly name: string;
  tileFaces(terrain: TerrainType): TileFaces;
  depositColor(deposit: DepositType): number | null;
}

const shade = (hex: number, factor: number): number => {
  const r = Math.min(255, Math.round(((hex >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((hex >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.round((hex & 0xff) * factor));
  return (r << 16) | (g << 8) | b;
};

/** Warm, slightly desaturated palette evoking the classic isometric look. */
const TERRAIN_TOP: Readonly<Record<TerrainType, number>> = {
  [TerrainType.DeepWater]: 0x2a5c8a,
  [TerrainType.ShallowWater]: 0x4a8bb5,
  [TerrainType.Sand]: 0xd9c48a,
  [TerrainType.Grass]: 0x7fa653,
  [TerrainType.Forest]: 0x4f7a3c,
  [TerrainType.Dirt]: 0x9a7b53,
  [TerrainType.Rock]: 0x8b8681,
  [TerrainType.Snow]: 0xeaf0f4,
  [TerrainType.Swamp]: 0x5c6b47,
};

const DEPOSIT_COLOR: Readonly<Record<DepositType, number | null>> = {
  [DepositType.None]: null,
  [DepositType.Forest]: 0x2f5222,
  [DepositType.StoneRock]: 0xb9b3ab,
  [DepositType.IronVein]: 0xb0703c,
  [DepositType.CoalSeam]: 0x2b2b2f,
  [DepositType.GoldVein]: 0xe8c357,
  [DepositType.FishShoal]: 0x9fd6e8,
};

export class PlaceholderAssets implements AssetProvider {
  readonly name = 'placeholder-v1';

  tileFaces(terrain: TerrainType): TileFaces {
    const top = TERRAIN_TOP[terrain];
    return { top, left: shade(top, 0.72), right: shade(top, 0.85) };
  }

  depositColor(deposit: DepositType): number | null {
    return DEPOSIT_COLOR[deposit];
  }
}
